import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export function managedArchivePath(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash
      || !['https://service.northinvent.com', window.location.origin].includes(url.origin)
      || !/^\/api\/quality\/archives\/[a-f0-9]{24}\/download$/.test(url.pathname)) return '';
    return url.pathname.slice(4);
  } catch { return ''; }
}
function externalUrl(value) {
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : ''; }
  catch { return ''; }
}

export default function QmsArchiveAsset({ asset, compact = false, preview = true }) {
  const { getToken } = useAuth();
  const url = asset?.url || asset?.archiveUrl || '', name = asset?.title || asset?.name || asset?.fileName || 'Attachment';
  const path = managedArchivePath(url), external = externalUrl(url), isImage = IMAGE_TYPES.includes(asset?.mimeType);
  const [imageUrl, setImageUrl] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const current = useRef(null);
  const load = useCallback(async () => {
    const state = current.current;
    if (!state || !path) return null;
    if (state.promise) return state.promise;
    setBusy(true); setError('');
    state.promise = (async () => {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to open this attachment.');
      const response = await window.fetch(`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}${path}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'omit', redirect: 'error', signal: state.controller.signal,
      });
      if (!response.ok) throw new Error(response.status === 403 || response.status === 404
        ? 'This attachment is unavailable or is no longer shared with you.' : 'The attachment could not be loaded. Try again.');
      const blob = await response.blob();
      if (state.controller.signal.aborted) return null;
      state.objectUrl = URL.createObjectURL(blob);
      if (IMAGE_TYPES.includes(blob.type)) setImageUrl(state.objectUrl);
      return state.objectUrl;
    })().catch(failure => {
      state.promise = null;
      if (!state.controller.signal.aborted) setError(failure.message || 'The attachment could not be loaded. Try again.');
      return null;
    }).finally(() => { if (!state.controller.signal.aborted) setBusy(false); });
    return state.promise;
  }, [getToken, path]);
  useEffect(() => {
    const state = { controller: new AbortController(), promise: null, objectUrl: '' };
    current.current = state; setImageUrl(''); setError(''); setBusy(false);
    if (path && preview && isImage) load();
    return () => { state.controller.abort(); if (state.objectUrl) URL.revokeObjectURL(state.objectUrl); if (current.current === state) current.current = null; };
  }, [path, preview, isImage, load]);
  async function download() {
    const objectUrl = await load();
    if (!objectUrl) return;
    const link = document.createElement('a'); link.href = objectUrl; link.download = name; document.body.appendChild(link); link.click(); link.remove();
  }
  const publicImage = !path && external && isImage && new URL(external).hostname === 'res.cloudinary.com' ? external : '';
  if (!path && !external) return <span>{name} — attachment unavailable</span>;
  return <article style={{ minWidth: 0, padding: compact ? '6px 0' : 12, border: compact ? undefined : '1px solid var(--ni-border-soft)', borderRadius: 8, overflowWrap: 'anywhere' }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <strong style={{ flex: '1 1 180px' }}>{name}</strong>
      {path ? <button type="button" className="ni-button" onClick={download} disabled={busy}>{busy ? 'Loading…' : 'Download'}</button>
        : <a href={external} target="_blank" rel="noopener noreferrer">Open attachment</a>}
      {asset?.size > 0 && <small style={{ color: 'var(--ni-text-muted)' }}>{asset.size < 1048576 ? `${Math.ceil(asset.size / 1024)} KB` : `${(asset.size / 1048576).toFixed(1)} MB`}</small>}
    </div>
    {preview && (imageUrl || publicImage) && <a href={imageUrl || publicImage} target="_blank" rel="noopener noreferrer" aria-label={`View ${name}`}><img src={imageUrl || publicImage} alt={name} loading="lazy" referrerPolicy="no-referrer"
      style={{ display: 'block', maxWidth: '100%', maxHeight: compact ? 200 : 420, objectFit: 'contain', marginTop: 10, borderRadius: 4 }} /></a>}
    {error && <p role="alert" style={{ marginBottom: 0 }}>{error}</p>}
  </article>;
}
